/**
 * "Second ticket parked" — the section above Gauge & Volume in the In Review panel
 * (OPEN-144 PR 3, §2a). Finds the job's open parked second tickets and shows the decision
 * for the oldest. Keep closes every one; Use closes this one and supersedes the rest.
 */

import React from 'react';
import { DUPLICATE_KIND } from './parked-kinds';
import { ParkedDecision } from './ParkedDecision';
import { useSyncConflicts } from './use-sync-conflicts';

export function ParkedTicketSection({ jobId }: { jobId: string }): React.JSX.Element | null {
  const { data, isLoading, isError } = useSyncConflicts({
    status: 'OPEN',
    kind: DUPLICATE_KIND,
    jobId,
  });
  if (isLoading) return null;
  const items = data?.pages.flatMap((p) => p.items) ?? [];
  if (isError) {
    return (
      <section aria-label="Second ticket parked" style={BOX}>
        <h3 style={TITLE}>Second ticket parked</h3>
        <p>Couldn’t load the parked ticket. Approve stays blocked until it is decided.</p>
      </section>
    );
  }
  // Oldest first: the list is newest first.
  const first = items[items.length - 1];
  if (first === undefined) return null;
  return (
    <section aria-label="Second ticket parked" style={BOX}>
      <h3 style={TITLE}>Second ticket parked</h3>
      <p style={{ fontSize: '13px', margin: '0 0 8px' }}>
        The driver sent a different run ticket after this one was saved. It was set aside, not
        applied. Keep the saved ticket or use the parked one before approving.
        {items.length > 1 &&
          ` There are ${String(items.length)} parked tickets on this job; this is the oldest.`}
      </p>
      <ParkedDecision id={first.id} />
    </section>
  );
}

const BOX: React.CSSProperties = {
  border: '1px solid var(--color-warning)',
  borderRadius: '8px',
  padding: '12px 14px',
  marginBottom: '16px',
};
const TITLE: React.CSSProperties = { fontSize: '15px', fontWeight: 600, margin: '0 0 6px' };
